import { useEffect, useState } from 'react';
import { Box, useBreakpointValue } from '@gemini/core';
import { useAppContext } from '@mfe-presentation/contexts';
import { mobileAppLinks } from '@mfe-helpers/mobileAppLinks';
import { correctBrandName } from '@mfe-helpers/portalHelpers';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';
import { default as downloadAppApple } from '@mfe-presentation/assets/svg/download-app-apple.svg';
import { default as downloadAppGoogle } from '@mfe-presentation/assets/svg/download-app-google.svg';
import { imagePaths } from '@mfe-helpers/mobileAppImages';

type Device = 'mobile' | 'tablet' | 'desktop';
const useDevice = () => {
    const device = useBreakpointValue({
        base: 'mobile',
        sm: 'tablet',
        xl: 'desktop',
    }) as Device;

    return {
        device,
        isMobile: device === 'mobile',
        isTablet: device === 'tablet',
        isDesktop: device === 'desktop',
    };
};

const WebToAppBanner = () => {
    const { portal } = useAppContext();
    const [imagePath, setImagePath] = useState<string | undefined>();
    const googlePlayLink = mobileAppLinks[portal]?.googlePlay;
    const appleStoreLink = mobileAppLinks[portal]?.appleStore;
    const { t } = useTranslation(NAME_SPACE);
    const { isMobile, isDesktop, isTablet } = useDevice();

    useEffect(() => {
        if (!portal) return;

        if (isMobile) {
            setImagePath(imagePaths[portal]?.mobile);
        } else if (isTablet) {
            setImagePath(imagePaths[portal]?.tablet);
        } else if (isDesktop) {
            setImagePath(imagePaths[portal]?.desktop);
        }
    }, [portal, isDesktop, isTablet, isMobile]);

    return (
        <Box
            display={{
                'breakpoint.base': 'block',
                'breakpoint.xl': 'flex',
            }}
            flexDirection={{
                'breakpoint.base': 'column',
                'breakpoint.xl': 'row-reverse',
            }}
            width="100%"
        >
            {imagePath && (
                <Box
                    display="flex"
                    height={320}
                    justifyContent="center"
                    width={{
                        'breakpoint.base': '100%',
                        'breakpoint.xl': '50%',
                    }}
                >
                    <Box
                        as="img"
                        borderRadius="radius.16"
                        height="100%"
                        width="auto"
                        objectFit="cover"
                        src={imagePath}
                        alt={t('step_results.resultCard.bannerToApp.image', {
                            replace: {
                                portal: correctBrandName[portal] ?? portal,
                            },
                        })}
                    />
                </Box>
            )}
            <Box
                width={{
                    'breakpoint.base': 'auto',
                    'breakpoint.xl': '50%',
                }}
                paddingRight="spacing.16"
                display={{
                    'breakpoint.base': 'block',
                    'breakpoint.xl': 'flex',
                }}
                flexDirection={{
                    'breakpoint.base': 'unset',
                    'breakpoint.xl': 'column',
                }}
                justifyContent="center"
                paddingTop={{
                    'breakpoint.base': 'spacing.24',
                    'breakpoint.xl': 'spacing.none',
                }}
                paddingLeft={{
                    'breakpoint.base': 'spacing.16',
                    'breakpoint.xl': 'spacing.none',
                }}
            >
                <Box>
                    <Box typography="typography.headline.24.bold">
                        {t('step_results.resultCard.bannerToApp.title')}
                    </Box>
                    <Box as="p" paddingTop="spacing.8">
                        {t('step_results.resultCard.bannerToApp.description')}
                    </Box>
                </Box>
                <Box
                    display={{
                        'breakpoint.base': 'block',
                        'breakpoint.xl': 'flex',
                    }}
                    paddingTop="spacing.24"
                >
                    <Box id="download-app-apple">
                        <Box as="a" href={appleStoreLink} target="_blank">
                            <img
                                data-testid="download-app-apple"
                                width="125"
                                height="42"
                                alt="Apple Store"
                                src={downloadAppApple}
                            />
                        </Box>
                    </Box>
                    <Box
                        id="download-app-google"
                        paddingLeft={{
                            'breakpoint.base': 'spacing.none',
                            'breakpoint.xl': 'spacing.16',
                        }}
                        paddingTop={{
                            'breakpoint.base': 'spacing.16',
                            'breakpoint.xl': 'spacing.none',
                        }}
                    >
                        <Box as="a" href={googlePlayLink} target="_blank" rel="noopener noreferrer">
                            <img
                                data-testid="download-app-google"
                                width="140"
                                height="42"
                                alt="Google Play"
                                src={downloadAppGoogle}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default WebToAppBanner;
